import { withErrorBoundary } from "@sentry/react";
import moment from "moment";
import { useContext } from "react";
import { MeetingViewMeetingNodeNewPageFragmentFragment } from "types/graphql-schema";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Error from "@components/error/error";
import { classNames } from "@helpers/css";
import { removeCurrentYear } from "@helpers/helpers";

import DetectedOneononeBanner from "./detected-oneonone-banner";
import MeetingPageHeader from "./header";
import { MeetingPageContext } from "./meeting-page-context";
import MeetingFinalizeButton from "./meeting-sidebar/meeting-finalize";
import { MeetingVideoConferenceImage } from "./meeting/video-conference-button";
import { getMeetingVideoConferenceName } from "./meeting/video-conference-helpers";
import PreviousMeetingsPopoverPanel from "./previous-meetings-popover-panel";
import PublishMeetingButton from "./publish-meeting-button";
import MeetingConvertToReview1on1BannerWithSentry from "./review/convert-to-review-1-on-1-banner";

const MeetingDetails = ({
  meeting,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
}) => {
  const { isInExtension } = useContext(MeetingPageContext);
  const meetingGroup = meeting.meetingGroup;

  if (!meetingGroup) return null;

  const meetingDate = meeting.startDatetime
    ? removeCurrentYear(moment(meeting.startDatetime).format("ddd, ll"))
    : "Unscheduled";

  return (
    <>
      <div
        className={classNames("flex flex-col gap-4", !isInExtension && "pb-2")}
      >
        <DetectedOneononeBanner meeting={meeting} />

        <div className="@2xl/layout-main:mt-4">
          <div className="flex gap-4 items-start justify-between mb-6">
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-1.5">
                <div className="tf-header-xl shrink-0">{meetingDate}</div>
                {meeting.draft && (
                  // 8px from the date (gap-1 + ml-1) — same-size header
                  // text but a regular weight in gray-400 to read as a
                  // subtler "Draft" annotation.
                  <div className="tf-header-xl font-normal text-gray-400 ml-1 shrink-0">
                    Draft
                  </div>
                )}
                {meeting.isRecurring && (
                  <PreviousMeetingsPopoverPanel
                    meeting={meeting}
                    hideDateButton
                  />
                )}
              </div>
              <div className="tf-body-xs-light truncate">{meeting.title}</div>
            </div>
            <div className="flex gap-1.5 items-center">
              {!isInExtension && meeting.videoConferenceUrl && (
                <Button
                  className="shrink-0 px-0 w-[24px]"
                  tooltip={`Join ${getMeetingVideoConferenceName({
                    meeting,
                    compact: false,
                  })}`}
                  url={meeting.videoConferenceUrl}
                  target="_blank"
                  size={ButtonSize.medium}
                  theme={buttonTheme.text}
                >
                  <MeetingVideoConferenceImage
                    meeting={meeting}
                    className="h-5 w-5"
                  />
                </Button>
              )}
              {!isInExtension && (
                <MeetingPageHeader
                  meetingGroup={meetingGroup}
                  meeting={meeting}
                />
              )}
              <div className="hidden @2xl/layout-main:contents">
                <MeetingFinalizeButton
                  meeting={meeting}
                  buttonProps={{ size: ButtonSize.medium }}
                />
              </div>
              {meeting.draft && (
                <PublishMeetingButton
                  meetingId={meeting.id}
                  meetingGroupId={meeting.meetingGroupId}
                  buttonProps={{
                    size: ButtonSize.medium,
                    theme: buttonTheme.primary,
                  }}
                >
                  Publish Meeting
                </PublishMeetingButton>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4 empty:hidden text-sm text-gray-900">
            {meeting.meetingGroup.isFormalOneonone &&
              meeting.canUpdate?.permission &&
              meeting.assessmentDeliveriesMatching1on1Template.edges.length ===
                0 && (
                <MeetingConvertToReview1on1BannerWithSentry meeting={meeting} />
              )}
          </div>
        </div>
      </div>
    </>
  );
};

const MeetingDetailsWithSentry = withErrorBoundary(MeetingDetails, {
  fallback: <Error description={"The meeting could not be rendered."} />,
});

export default MeetingDetailsWithSentry;
